import React from 'react';
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { ChevronRightIcon, PlayIcon } from '@heroicons/react/24/outline';
import { useEffect } from "react";
import BannerImg from "../Images/Banner/Honeycomb-Website-Banner-Image.jpeg";

function Counter({ from = 0, to }) {
  const count = useMotionValue(from);
  const formatted = useTransform(count, latest => {
    if (latest >= 1_000_000) {
      return (latest / 1_000_000).toFixed(1).replace(/\.0$/, '') + "M";
    } else if (latest >= 1_000) {
      return (latest / 1_000).toFixed(1).replace(/\.0$/, '') + "K";
    }
    return Math.floor(latest);
  });

  useEffect(() => {
    const controls = animate(count, to, { duration: 6, ease: "easeOut" });
    return controls.stop;
  }, [count, to]);

  return <motion.span>{formatted}</motion.span>;
}



const Hero = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${BannerImg})`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-honeycomb-blue-900/80 via-honeycomb-grey-900/70 to-honeycomb-blue-800/80" />
        
        {/* Animated Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="pt-16 space-y-8"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20"
          >
            <span className="text-honeycomb-blue-200 text-sm font-medium">
              🌍 Apparel Manufacturing Since 2001
            </span>
          </motion.div>

          {/* Main Headlines */}
          <div className="space-y-4">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white leading-tight"
            >
              Crafting Global
              <br />
              <span className="bg-gradient-to-r from-honeycomb-blue-400 to-honeycomb-blue-200 bg-clip-text text-transparent">
                Fashion Excellence
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-lg sm:text-xl lg:text-2xl text-honeycomb-blue-100 font-light max-w-3xl mx-auto leading-relaxed"
            >
              Premium Apparel manufacturing for men, women, and children worldwide. 
              From concept to delivery, we bring your fashion vision to life with 
              <span className="text-white font-semibold"> uncompromising quality</span> and 
              <span className="text-white font-semibold"> reliable delivery</span>.
            </motion.p>
          </div>
          
          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-8 pt-8 max-w-4xl mx-auto"
          >
            {[
              { number: `${new Date().getFullYear() - 2001}+`, label: 'Years Experience' },
              { number: '30+', label: "Countries Served" },
              { number: '6000+', label: 'Style Delivered' },
              { number: '144000000+', label: 'Retail Value Delivered' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1 + index * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <div className="text-3xl sm:text-4xl font-bold text-white mb-2">
                 {stat.label === "Retail Value Delivered" && <span>$</span>}
                  <Counter from={0} to={parseInt(stat.number)} />
                 {stat.number.replace(/[0-9]/g, "")}
                </div>
                <div className="text-honeycomb-blue-200 text-sm sm:text-base font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

      {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 pt-8"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group bg-honeycomb-blue-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-honeycomb-blue-700 transition-all duration-300 flex items-center shadow-2xl btn-primary"
              onClick={() => {
                document.querySelector('#portfolio')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }}
            >
              <PlayIcon className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
              View Portfolio
            </motion.button>
          </motion.div>

      {/* Scroll Indicator 
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
        >
          <div className="w-1 h-3 bg-white/60 rounded-full mt-2"></div>
        </motion.div>
      </motion.div>
     */} 
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
